import { CheckCircle, CircleNotch, UploadSimple } from "@phosphor-icons/react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getApiErrorMessage, type SetupImport, useImportHousehold } from "@/lib/api";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Narrow shape used to render the parsed-preview summary; not the full SetupImport contract. */
interface ParsedSetupPreview {
  setupData: {
    household?: { name?: string };
    email?: string;
    rooms?: Array<{ roomName?: string; windows?: unknown[]; devices?: unknown[] }>;
    devices?: unknown[];
  };
}

function isParsedSetupPreview(value: unknown): value is ParsedSetupPreview {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const setupData = (value as { setupData?: unknown }).setupData;
  if (typeof setupData !== "object" || setupData === null) {
    return false;
  }
  return "rooms" in setupData || "devices" in setupData || "household" in setupData;
}

export function ImportHouseholdDialog({ open, onOpenChange }: Props) {
  const importMutation = useImportHousehold();
  const navigate = useNavigate();
  const [rawText, setRawText] = useState("");
  const [error, setError] = useState("");

  let parsed: ParsedSetupPreview | null = null;
  let parseFailed = false;
  if (rawText.trim() !== "") {
    try {
      const value: unknown = JSON.parse(rawText);
      if (isParsedSetupPreview(value)) {
        parsed = value;
      } else {
        parseFailed = true;
      }
    } catch {
      parseFailed = true;
    }
  }

  const rooms = parsed?.setupData.rooms ?? [];
  const roomCount = rooms.length;
  const windowCount = rooms.reduce((sum, r) => sum + (r.windows?.length ?? 0), 0);
  const deviceCount =
    rooms.reduce((sum, r) => sum + (r.devices?.length ?? 0), 0) +
    (parsed?.setupData.devices?.length ?? 0);
  const roomNames = rooms.map((r) => r.roomName).filter((name): name is string => Boolean(name));

  const resetForm = () => {
    setRawText("");
    setError("");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const text = await file.text();
    setRawText(text);
    e.target.value = "";
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    let value: unknown;
    try {
      value = JSON.parse(rawText);
    } catch {
      setError("Paste or upload valid JSON before creating the household.");
      return;
    }
    try {
      const data = await importMutation.mutateAsync(value as SetupImport);
      resetForm();
      onOpenChange(false);
      if (data) {
        void navigate(`/admin/households/${data.id}`);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to import household."));
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      resetForm();
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New household</DialogTitle>
          <DialogDescription>
            Import a <span className="font-mono">setupData</span> JSON to populate the whole home at
            once, then confirm the seed details.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="setupJsonFile" className="text-foreground text-sm font-medium">
              Upload setupData.json <span className="text-muted">(optional)</span>
            </label>
            <input
              id="setupJsonFile"
              type="file"
              accept="application/json"
              onChange={(e) => void handleFileChange(e)}
              className="text-muted file:border-input file:bg-card file:text-primary w-full text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-[13px] file:font-medium"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="setupJsonText" className="text-foreground text-sm font-medium">
              Or paste JSON
            </label>
            <textarea
              id="setupJsonText"
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
              rows={6}
              placeholder='{ "setupData": { "household": { "name": "..." }, "email": "...", "rooms": [...] } }'
              className="border-input bg-card text-foreground placeholder:text-muted focus-visible:ring-ring w-full rounded-xl border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            />
          </div>

          {parsed && (
            <div className="bg-success/10 flex items-start gap-2.5 rounded-xl p-3">
              <CheckCircle className="text-success mt-0.5 h-4 w-4 shrink-0" weight="light" />
              <div>
                <p className="text-primary text-[13.5px] font-semibold">
                  Parsed — {roomCount} rooms · {windowCount} windows · {deviceCount} devices
                </p>
                {roomNames.length > 0 && (
                  <p className="text-muted mt-0.5 text-[12.5px]">{roomNames.join(", ")}</p>
                )}
              </div>
            </div>
          )}
          {!parsed && parseFailed && (
            <p className="text-muted text-[13px]">
              Couldn&apos;t read that as a <span className="font-mono">setupData</span> payload yet
              — keep editing.
            </p>
          )}

          {parsed && (
            <div className="space-y-2">
              <div className="space-y-1">
                <p className="text-primary text-[12.5px] font-semibold">
                  Home name <span className="text-muted font-normal">(from JSON)</span>
                </p>
                <p className="text-muted text-[14px]">{parsed.setupData.household?.name ?? "—"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-primary text-[12.5px] font-semibold">
                  Contact email <span className="text-muted font-normal">(from JSON)</span>
                </p>
                <p className="text-muted text-[14px]">{parsed.setupData.email ?? "—"}</p>
              </div>
            </div>
          )}

          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={importMutation.isPending}>
              {importMutation.isPending ? (
                <CircleNotch className="h-4 w-4 animate-spin" weight="light" />
              ) : (
                <>
                  <UploadSimple className="mr-2 h-4 w-4" weight="light" />
                  Create household
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
